<template>
  <section class="lg:py-24 py-12 bg-gray-50">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Main Heading -->
      <div class="text-center mb-16 lg:mb-20">
        <h2
          class="text-2xl sm:text-3xl md:text-4xl font-heading font-semibold text-gray-900 leading-tight max-w-4xl mx-auto"
        >
          Turn noise <br class="block" />
          <span class="text-emerald-500">into Growth Signals</span>
        </h2>
      </div>

      <!-- Benefits Grid -->
      <div
        class="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 max-w-7xl mx-auto"
      >
        <!-- Benefit 1: Easy Dashboard Creation -->
        <div class="group flex flex-col items-center text-center">
          <div
            class="w-full aspect-[1.1] mb-6 overflow-hidden rounded-2xl bg-[#1D1E20] transition-transform duration-300 group-hover:-translate-y-1 group-hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-800 flex items-center justify-center"
          >
            <img
              :src="dashboardImage"
              alt="Beautiful dashboard visualization"
              class="w-[250%] max-w-none dashboard-float"
            />
          </div>
          <div>
            <h3 class="text-lg font-semibold text-gray-900 mb-3">
              Beautiful Dashboards
            </h3>
            <p class="text-gray-600 max-w-[270px] mx-auto leading-relaxed">
              Stunning, interactive dashboards that turn complex data into
              clear, actionable insights for your team.
            </p>
          </div>
        </div>

        <!-- Benefit 2: Data Integration -->
        <div class="group flex flex-col items-center text-center">
          <div
            class="w-full aspect-[1.1] mb-6 overflow-hidden rounded-2xl bg-white transition-transform duration-300 group-hover:-translate-y-1 group-hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-emerald-100"
          >
            <div class="w-full h-full flex items-center justify-center p-4">
              <!-- Integration Icons Animation -->
              <div
                class="integration-grid w-full h-full flex flex-col justify-center"
              >
                <!-- Top row -->
                <div class="overflow-hidden mb-6">
                  <div class="scroll-back-forth flex gap-6 w-full">
                    <div
                      v-for="icon in [...topRowIcons, ...topRowIcons]"
                      :key="icon.name"
                      class="flex-none w-16 h-16 bg-gray-50 rounded-xl shadow-sm flex items-center justify-center p-3"
                    >
                      <img
                        :src="icon.src"
                        :alt="icon.name"
                        class="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                </div>

                <!-- Middle row -->
                <div class="overflow-hidden mb-6">
                  <div class="scroll-back-forth-delayed flex gap-6 w-full">
                    <div
                      v-for="icon in [...middleRowIcons, ...middleRowIcons]"
                      :key="icon.name"
                      class="flex-none w-16 h-16 bg-gray-50 rounded-xl shadow-sm flex items-center justify-center p-3"
                    >
                      <img
                        :src="icon.src"
                        :alt="icon.name"
                        class="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                </div>

                <!-- Bottom row -->
                <div class="overflow-hidden mb-6">
                  <div class="scroll-back-forth-more-delayed flex gap-6 w-full">
                    <div
                      v-for="icon in [...bottomRowIcons, ...bottomRowIcons]"
                      :key="icon.name"
                      class="flex-none w-16 h-16 bg-gray-50 rounded-xl shadow-sm flex items-center justify-center p-3"
                    >
                      <img
                        :src="icon.src"
                        :alt="icon.name"
                        class="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                </div>

                <!-- Fourth row for better fill -->
                <!-- <div class="overflow-hidden">
                  <div class="scroll-back-forth flex gap-6 w-full">
                    <div
                      v-for="icon in [...extraRowIcons, ...extraRowIcons]"
                      :key="icon.name"
                      class="flex-none w-16 h-16 bg-gray-50 rounded-xl shadow-sm flex items-center justify-center p-3"
                    >
                      <img
                        :src="icon.src"
                        :alt="icon.name"
                        class="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                </div> -->
              </div>
            </div>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-gray-900 mb-3">
              All your data in one place
            </h3>
            <p class="text-gray-600 max-w-[300px] mx-auto leading-relaxed">
              Connect all your data sources. From GA4 to Shopify, get everything
              in one centralized location.
            </p>
          </div>
        </div>

        <!-- Benefit 3: Real-time Monitoring -->
        <div class="group flex flex-col items-center text-center">
          <div
            class="w-full aspect-[1.1] mb-6 overflow-hidden rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 transition-transform duration-300 group-hover:-translate-y-1 group-hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-emerald-100"
          >
            <div class="w-full h-full flex items-center justify-center p-6">
              <!-- Clean monitoring interface -->
              <div class="w-full h-full flex flex-col justify-center space-y-6">
                <!-- Monitoring metrics -->
                <div class="bg-white rounded-2xl p-5 shadow-sm">
                  <div class="flex items-center justify-between mb-3">
                    <div class="flex items-center space-x-2">
                      <!-- <div
                        class="w-2 h-2 bg-green-400 rounded-full animate-pulse"
                      ></div> -->
                      <!-- <span class="text-sm font-medium text-gray-700"
                        >Live Monitoring</span
                      > -->
                    </div>
                    <div class="text-xs text-gray-500">3h ago</div>
                  </div>
                  <div class="text-lg font-semibold text-gray-900">
                    {{ currentMetric.label }}
                  </div>
                  <div class="text-2xl font-bold text-emerald-600">
                    +{{ animatedValue.toFixed(1) }}{{ currentMetric.suffix }}
                  </div>
                </div>

                <!-- Alert notification -->
                <div class="bg-white rounded-2xl p-5 shadow-sm">
                  <div class="flex items-center space-x-3">
                    <div
                      class="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center"
                    >
                      <svg
                        class="w-5 h-5 text-orange-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clip-rule="evenodd"
                        ></path>
                      </svg>
                    </div>
                    <div class="flex-1 text-left">
                      <div class="text-sm font-medium text-gray-900">
                        Conversion Alert
                      </div>
                      <div class="text-xs text-gray-600">
                        Checkout abandonment down 8%
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-gray-900 mb-3">
              Smart Monitoring & Alerts
            </h3>
            <p class="text-gray-600 max-w-[300px] mx-auto leading-relaxed">
              Never miss important changes. Get notified when your data shows
              opportunities or issues.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import gsap from "gsap";

// Import images from assets
import dashboardImage from "@/assets/img/img-beautiful-dash-zoom.png";

// Import integration icons for animation
import ga4Icon from "@/assets/section/ga4.png";
import slackIcon from "@/assets/section/slack.svg";
import sheetsIcon from "@/assets/section/sheets.svg";
import sqlIcon from "@/assets/section/sql.svg";
import posthogIcon from "@/assets/section/posthog.png";
import stripeIcon from "@/assets/section/stripe.webp";
import shopifyIcon from "@/assets/section/shopify.png";
import mailchimpIcon from "@/assets/section/mailchimp.png";
import hubspotIcon from "@/assets/section/hubspot.webp";
import facebookIcon from "@/assets/section/facebook.svg";
import airtableIcon from "@/assets/section/airtable.svg";
import asanaIcon from "@/assets/section/asana.svg";

// Animation data - smaller selection for the benefit card
const topRowIcons = [
  { name: "Google Analytics 4", src: ga4Icon },
  { name: "Google Sheets", src: sheetsIcon },
  { name: "Slack", src: slackIcon },
  { name: "Airtable", src: airtableIcon },
];

const middleRowIcons = [
  { name: "PostHog", src: posthogIcon },
  { name: "Stripe", src: stripeIcon },
  { name: "Shopify", src: shopifyIcon },
  { name: "Facebook", src: facebookIcon },
];

const bottomRowIcons = [
  { name: "Mailchimp", src: mailchimpIcon },
  { name: "HubSpot", src: hubspotIcon },
  { name: "Asana", src: asanaIcon },
  { name: "SQL Database", src: sqlIcon },
];

const metrics = [
  { label: "Website Traffic", value: 6.4, suffix: "%" },
  { label: "Add to Cart", value: 8.6, suffix: "%" },
  { label: "Conversion Rate", value: 4.2, suffix: "%" },
  { label: "Average Order Value", value: 7.9, suffix: "%" },
];

const currentMetricIndex = ref(0);
const animatedValue = ref(metrics[0].value);
let intervalId = null;

const currentMetric = ref(metrics[0]);

onMounted(() => {
  intervalId = setInterval(() => {
    currentMetricIndex.value = (currentMetricIndex.value + 1) % metrics.length;
    const nextMetric = metrics[currentMetricIndex.value];
    currentMetric.value = nextMetric;

    gsap.to(
      { value: animatedValue.value },
      {
        value: nextMetric.value,
        duration: 0.8,
        ease: "power2.inOut",
        onUpdate: function () {
          animatedValue.value = parseFloat(this.targets()[0].value.toFixed(1));
        },
      }
    );
  }, 3000); // Change metric every 3 seconds
});

onUnmounted(() => {
  if (intervalId) {
    clearInterval(intervalId);
  }
});
</script>

<style scoped>
.integration-grid {
  position: relative;
  overflow: hidden;
}

.scroll-back-forth {
  animation: moveBackAndForth 12s ease-in-out infinite;
}

.scroll-back-forth-delayed {
  animation: moveBackAndForth 12s ease-in-out infinite;
  animation-delay: -4s;
}

.scroll-back-forth-more-delayed {
  animation: moveBackAndForth 12s ease-in-out infinite;
  animation-delay: -8s;
}

@keyframes moveBackAndForth {
  0%,
  100% {
    transform: translateX(0);
  }
  50% {
    transform: translateX(-100px);
  }
}

.dashboard-float {
  animation: dashboard-pan-zoom 20s cubic-bezier(0.37, 0, 0.63, 1) infinite
    alternate;
}

@keyframes dashboard-pan-zoom {
  from {
    transform: scale(1) translateX(-130px);
  }
  to {
    transform: scale(1.15) translateX(130px);
  }
}
</style>
